import type { HttpClient } from '../http.js';
import type {
  CheckoutSession,
  LineItem,
  ShippingRate,
  Address,
  CheckoutCustomer,
  CreateCheckoutSessionOptions,
} from '../types.js';

/**
 * Result returned by `updateCart()`. Mirrors the
 * `POST /checkout-sessions/:id/line-items` payload (mutation stats, NOT
 * a refreshed session). Call `loadSession()` to get an updated session.
 */
export interface UpdateCartResult {
  success: boolean;
  /** Number of existing line items updated. */
  updated: number;
  /** Number of new line items created. */
  created: number;
  /** Total number of line items in the cart after the mutation. */
  total: number;
}

/**
 * Manages checkout sessions — load, update cart, apply promo codes, handle shipping.
 *
 * Works for both **shipped** and **digital** products:
 *
 * **Shipped products** (physical goods):
 * ```
 * updateCustomer(id, { email, firstName, lastName })
 * updateAddress(id, { shippingAddress: { line1, city, ... } })
 * getShippingRates(id)
 * selectShippingRate(id, rateId)
 * ```
 *
 * **Digital products** (no shipping needed):
 * ```
 * updateCustomer(id, { email, firstName, lastName })
 * updateAddress(id, { billingAddress: { line1, city, country } })
 * // Skip shipping rates — go straight to payment
 * ```
 */
export class CheckoutModule {
  private sessionToken: string | undefined;
  private customerEmail: string | undefined;
  private customerFirstName: string | undefined;
  private customerLastName: string | undefined;
  private customerPhone: string | undefined;
  /**
   * Last session returned by `loadSession()`. Cached so sibling modules
   * (e.g. `FunnelModule`) can read the cart without re-fetching. Cleared
   * intentionally on no events — call `loadSession()` again to refresh.
   */
  private _currentSession: CheckoutSession | undefined;

  constructor(
    private readonly http: HttpClient,
    private readonly storeId: string,
    private readonly onTokenChange?: (token: string) => void,
  ) {}

  /**
   * Returns the most recently loaded `CheckoutSession`, if any.
   *
   * Populated by `loadSession()`. Returns `undefined` if the SDK has not
   * loaded a session yet. Used by `funnel.navigate()` to source the cart.
   */
  getCurrentSession(): CheckoutSession | undefined {
    return this._currentSession;
  }

  /**
   * Load an existing checkout session by its checkout token.
   *
   * @param checkoutToken The checkout token (from URL param `checkoutToken`).
   * @param sessionToken The JWT session token (from URL param `token`). Required for auth.
   * @param opts.currency Optional presentment currency override (e.g. 'EUR').
   *   Only applied when the session has no currency set yet (first load).
   *   Once set, currency is locked to the session and this param is ignored.
   *
   * Both `checkoutToken` and `sessionToken` come from the redirect URL returned by `createSession()`.
   * Example URL: `/checkout?checkoutToken=abc123&token=eyJ...`
   */
  async loadSession(
    checkoutToken: string,
    sessionToken?: string,
    opts?: { currency?: string },
  ): Promise<CheckoutSession> {
    if (sessionToken) {
      this.sessionToken = sessionToken;
      this.onTokenChange?.(sessionToken);
    }

    const params: Record<string, string> = { skipAsyncWait: 'false' };
    if (opts?.currency) {
      params.currency = opts.currency;
    }

    const raw = await this.http.get<{ checkoutSession: unknown; summary: unknown }>(
      `/api/v1/checkout-sessions/${checkoutToken}/v2`,
      {
        params,
        token: this.sessionToken,
      },
    );

    const session = this.normalizeSession(raw);
    this._currentSession = session;
    return session;
  }

  /**
   * Create a new checkout session.
   *
   * Returns a `checkoutToken` and `sessionToken` (JWT) needed for all subsequent calls.
   * Also returns a `checkoutUrl` (the full Tagada-hosted checkout URL, for reference).
   */
  async createSession(
    opts: CreateCheckoutSessionOptions,
  ): Promise<{ checkoutUrl: string; checkoutToken: string; sessionToken: string; customerId: string }> {
    const lineItems = opts.items.map((i) => ({
      variantId: i.variantId,
      priceId: i.priceId,
      quantity: i.quantity,
    }));

    const body: Record<string, unknown> = {
      storeId: this.storeId,
      lineItems,
    };
    if (opts.returnUrl) body.returnUrl = opts.returnUrl;
    if (opts.discountCodes?.length) body.discountCodes = opts.discountCodes;
    if (opts.metadata && Object.keys(opts.metadata).length > 0) body.metadata = opts.metadata;
    if (opts.customerMetadata && Object.keys(opts.customerMetadata).length > 0) {
      body.customerMetadata = opts.customerMetadata;
    }
    if (opts.customerTags?.length) body.customerTags = opts.customerTags;

    // Always send currency in customer object (backend reads customer.currency)
    const customerData: Record<string, unknown> = {};
    if (opts.currency) customerData.currency = opts.currency;
    if (opts.customerEmail) {
      customerData.email = opts.customerEmail;
      customerData.firstName = opts.customerFirstName;
      customerData.lastName = opts.customerLastName;
    }
    if (Object.keys(customerData).length > 0) {
      body.customer = customerData;
    }

    const res = await this.http.post<{
      checkoutUrl: string;
      checkoutToken: string;
      customerId: string;
    }>('/api/v1/checkout/session/init', body);

    const url = new URL(res.checkoutUrl);
    const sessionToken = url.searchParams.get('token') ?? '';
    this.sessionToken = sessionToken;
    if (sessionToken) this.onTokenChange?.(sessionToken);

    return {
      checkoutUrl: res.checkoutUrl,
      checkoutToken: res.checkoutToken,
      sessionToken,
      customerId: res.customerId,
    };
  }

  /**
   * Create a session and produce a self-hosted redirect URL.
   *
   * Convenience wrapper around `createSession()`. Builds a URL of the
   * form `${origin}${checkoutPath}?checkoutToken=...&sessionToken=...`
   * that you can navigate to from a Cart page. The receiving page reads
   * the params via {@link CheckoutModule.parseTokensFromUrl} and calls
   * {@link CheckoutModule.loadSession}.
   *
   * For Tagada-hosted checkouts use the `checkoutUrl` returned by
   * `createSession()` directly — this helper is for **self-hosted**
   * checkout pages.
   *
   * @example
   * ```ts
   * const { url } = await tagada.checkout.createSessionUrl({
   *   items: [{ variantId, quantity: 1 }],
   *   checkoutPath: '/checkout',
   * });
   * window.location.href = url;
   * ```
   */
  async createSessionUrl(
    opts: CreateCheckoutSessionOptions & {
      /** Path on the current origin for the self-hosted checkout. Default: `/checkout`. */
      checkoutPath?: string;
      /** Origin to use. Defaults to `window.location.origin`. */
      origin?: string;
    },
  ): Promise<{
    url: string;
    checkoutUrl: string;
    checkoutToken: string;
    sessionToken: string;
    customerId: string;
  }> {
    const { checkoutPath = '/checkout', origin: originOverride, ...sessionOpts } = opts;
    const session = await this.createSession(sessionOpts);

    const origin =
      originOverride ?? (typeof window !== 'undefined' ? window.location.origin : '');
    const params = new URLSearchParams({
      checkoutToken: session.checkoutToken,
      sessionToken: session.sessionToken,
    });
    const url = `${origin}${checkoutPath}?${params.toString()}`;

    return { ...session, url };
  }

  /**
   * Read `checkoutToken` and session token from a URL.
   *
   * Recognizes both the SDK convention (`?sessionToken=`) and the
   * Tagada-hosted convention (`?token=`), so the same checkout page
   * works whether the user came from `createSessionUrl()` or directly
   * from a Tagada-hosted redirect.
   *
   * Returns `null` if `checkoutToken` is missing.
   *
   * @example
   * ```ts
   * const tokens = CheckoutModule.parseTokensFromUrl();
   * if (!tokens) {
   *   window.location.href = '/cart';
   *   return;
   * }
   * const session = await tagada.checkout.loadSession(
   *   tokens.checkoutToken,
   *   tokens.sessionToken,
   * );
   * ```
   */
  static parseTokensFromUrl(
    url?: string,
  ): { checkoutToken: string; sessionToken: string } | null {
    if (typeof url === 'undefined' && typeof window === 'undefined') return null;
    const href = url ?? window.location.href;
    let parsed: URL;
    try {
      parsed = new URL(href);
    } catch {
      return null;
    }
    const checkoutToken = parsed.searchParams.get('checkoutToken');
    if (!checkoutToken) return null;
    const sessionToken =
      parsed.searchParams.get('sessionToken') ?? parsed.searchParams.get('token') ?? '';
    return { checkoutToken, sessionToken };
  }

  /** Instance alias of {@link CheckoutModule.parseTokensFromUrl}. */
  parseTokensFromUrl(url?: string) {
    return CheckoutModule.parseTokensFromUrl(url);
  }

  /**
   * Add or update cart items on an existing session (upsert by product).
   *
   * Sends each item as `lineItems` to `POST /checkout-sessions/:id/line-items`.
   * The endpoint UPSERTs by product: it adds new products and updates the
   * variant/quantity of products already in the cart, but it does NOT remove
   * products omitted from `items`, and an empty array is a no-op (it does not
   * clear the cart). Each `quantity` must be >= 1 — removal is not supported by
   * this endpoint.
   *
   * Returns a server-side mutation summary (counts of created/updated rows).
   * It does NOT return the updated `CheckoutSession` — the underlying endpoint
   * never has and the previous typing was a lie that quietly assigned
   * `{ success, updated, ... }` into a `CheckoutSession` slot in `useCheckout`.
   *
   * Call `loadSession()` after this if you need the refreshed session
   * (the React `useCheckout` hook does this for you).
   */
  async updateCart(sessionId: string, items: LineItem[]): Promise<UpdateCartResult> {
    const lineItems = items.map((i) => ({
      variantId: i.variantId,
      priceId: i.priceId,
      quantity: i.quantity,
    }));

    return this.http.post(
      `/api/v1/checkout-sessions/${sessionId}/line-items`,
      { lineItems },
      { token: this.sessionToken },
    );
  }

  /**
   * Update customer identity (email, name, phone).
   *
   * Call this before `updateAddress()` — the SDK remembers the values and
   * includes them automatically when addresses are submitted.
   *
   * If you prefer a single call, use `updateCustomerAndAddress()` instead.
   */
  async updateCustomer(
    sessionId: string,
    customer: Partial<CheckoutCustomer>,
  ): Promise<void> {
    if (customer.email) this.customerEmail = customer.email;
    if (customer.firstName) this.customerFirstName = customer.firstName;
    if (customer.lastName) this.customerLastName = customer.lastName;
    if (customer.phone) this.customerPhone = customer.phone;
  }

  /**
   * Update shipping and/or billing address on the session.
   *
   * Automatically includes customer name/phone stored from `updateCustomer()`.
   * Uses the same atomic API call as the production checkout, so email,
   * customer name, and addresses are all persisted together.
   *
   * **Shipped products**: pass `shippingAddress`. If `billingAddress` is omitted,
   * the shipping address is automatically used as billing.
   *
   * **Digital products**: pass only `billingAddress` (no shipping needed).
   *
   * @example
   * // Shipped product — billing = shipping automatically
   * await checkout.updateAddress(sessionId, {
   *   shippingAddress: { line1: '10 Rue de la Paix', city: 'Paris', postalCode: '75002', country: 'FR' },
   * });
   *
   * @example
   * // Digital product — billing only
   * await checkout.updateAddress(sessionId, {
   *   billingAddress: { country: 'FR' },
   * });
   */
  async updateAddress(
    sessionId: string,
    addresses: { shippingAddress?: Address; billingAddress?: Address },
  ): Promise<void> {
    const shipping = this.mapAddress(addresses.shippingAddress);
    const billing = this.mapAddress(addresses.billingAddress);

    const effectiveShipping = shipping ?? billing ?? {};
    const effectiveBilling = billing ?? shipping;
    const differentBilling = !!(addresses.billingAddress && addresses.shippingAddress);

    await this.http.post(
      `/api/v1/checkout-sessions/${sessionId}/customer-and-session-info`,
      {
        checkoutSessionId: sessionId,
        customerData: {
          email: this.customerEmail,
        },
        shippingAddress: effectiveShipping,
        billingAddress: effectiveBilling ?? {},
        differentBillingAddress: differentBilling,
      },
      { token: this.sessionToken },
    );
  }

  /**
   * Set customer info + addresses in one call (mirrors production checkout).
   *
   * Equivalent to calling `updateCustomer()` then `updateAddress()`, but
   * in a single network request.
   */
  async updateCustomerAndAddress(
    sessionId: string,
    data: {
      customer: Partial<CheckoutCustomer>;
      shippingAddress?: Address;
      billingAddress?: Address;
    },
  ): Promise<void> {
    if (data.customer.email) this.customerEmail = data.customer.email;
    if (data.customer.firstName) this.customerFirstName = data.customer.firstName;
    if (data.customer.lastName) this.customerLastName = data.customer.lastName;
    if (data.customer.phone) this.customerPhone = data.customer.phone;

    const shipping = this.mapAddress(data.shippingAddress);
    const billing = this.mapAddress(data.billingAddress);

    const effectiveShipping = shipping ?? billing ?? {};
    const effectiveBilling = billing ?? shipping;
    const differentBilling = !!(data.billingAddress && data.shippingAddress);

    await this.http.post(
      `/api/v1/checkout-sessions/${sessionId}/customer-and-session-info`,
      {
        checkoutSessionId: sessionId,
        customerData: {
          email: this.customerEmail,
        },
        shippingAddress: effectiveShipping,
        billingAddress: effectiveBilling ?? {},
        differentBillingAddress: differentBilling,
      },
      { token: this.sessionToken },
    );
  }

  /** Map an SDK Address to the API shape, injecting stored customer fields. */
  private mapAddress(a?: Address): Record<string, unknown> | undefined {
    if (!a) return undefined;
    return stripEmpty({
      firstName: a.firstName ?? this.customerFirstName,
      lastName: a.lastName ?? this.customerLastName,
      phone: a.phone ?? this.customerPhone,
      address1: a.line1,
      address2: a.line2,
      city: a.city,
      state: a.state,
      postal: a.postalCode,
      country: a.country,
    });
  }

  /** Apply a promotion code to the session. */
  async applyPromoCode(sessionId: string, code: string): Promise<void> {
    await this.http.post(
      `/api/v1/checkout-sessions/${sessionId}/promotions/apply`,
      { checkoutSessionId: sessionId, code },
      { token: this.sessionToken },
    );
  }

  /** Remove a promotion from the session by promotion ID. */
  async removePromoCode(sessionId: string, promotionId?: string): Promise<void> {
    const promoId = promotionId ?? 'default';
    await this.http.delete(
      `/api/v1/checkout-sessions/${sessionId}/promotions/${promoId}`,
      { token: this.sessionToken },
    );
  }

  /**
   * Fetch available shipping rates for the session's address.
   * Only relevant for **shipped products** — digital products can skip this step.
   *
   * Requires a shipping address to be set first via `updateAddress()`.
   * Returns an empty array if no rates are configured or address is missing.
   */
  async getShippingRates(sessionId: string): Promise<ShippingRate[]> {
    const res = await this.http.get<{ rates: Array<Record<string, unknown>> }>(
      `/api/v1/checkout-sessions/${sessionId}/shipping-rates`,
      { token: this.sessionToken },
    );
    return (res.rates ?? []).map((r) => this.normalizeShippingRate(r));
  }

  /**
   * Normalize the v1 rates payload (`shippingRateName`, `estimatedDeliveryTime`)
   * to the SDK's `ShippingRate` shape (`name`, `estimatedDays`). Without
   * this the consumer's UI shows blank rate names — the demo's "Delivery"
   * radios were rendering only the price, no label.
   */
  private normalizeShippingRate(r: Record<string, unknown>): ShippingRate {
    return {
      id: r.id as string,
      name: (r.name ?? r.shippingRateName ?? '') as string,
      description: (r.description ?? undefined) as string | undefined,
      amount: (r.amount ?? 0) as number,
      currency: (r.currency ?? 'USD') as string,
      estimatedDays: (r.estimatedDays ?? r.estimatedDeliveryTime ?? undefined) as number | undefined,
    };
  }

  /**
   * Select a shipping rate for the session.
   * Only relevant for **shipped products** — digital products can skip this step.
   */
  async selectShippingRate(sessionId: string, rateId: string): Promise<void> {
    await this.http.post(
      `/api/v1/checkout-sessions/${sessionId}/shipping-rate`,
      { checkoutSessionId: sessionId, shippingRateId: rateId },
      { token: this.sessionToken },
    );
  }

  /**
   * Normalize the raw v2 API response into a cleaner CheckoutSession shape.
   * The raw response has nested `checkoutSession` + `summary` -- we flatten it.
   */
  private normalizeSession(raw: { checkoutSession: unknown; summary: unknown }): CheckoutSession {
    const cs = raw.checkoutSession as Record<string, unknown>;
    const orderSummaries = (cs.orderSummaries ?? []) as Array<Record<string, unknown>>;
    const summary = (raw.summary ?? cs.summary ?? orderSummaries[0] ?? {}) as Record<string, unknown>;
    const lineItems = (cs.sessionLineItems ?? []) as Array<Record<string, unknown>>;
    const customer = cs.customer as Record<string, unknown> | undefined;
    const sessionCurrency = ((summary.currency ?? cs.selectedPresentmentCurrency) as string | undefined)?.toUpperCase();

    const items = lineItems.map((li) => {
      const price = li.price as Record<string, unknown> | undefined;
      // The v2 endpoint returns `product` and `variant` as siblings of
      // `price` on each line item — not nested under `price`. Reading
      // them from `price.variant.product` (the old code) silently gave
      // back empty objects, which is why the order summary rendered
      // blank product names and a placeholder image instead of the
      // actual product art.
      const variant = (li.variant ?? price?.variant ?? {}) as Record<string, unknown>;
      const product = (li.product ?? variant.product ?? {}) as Record<string, unknown>;
      const currencyOpts = (price?.currencyOptions ?? {}) as Record<string, { amount: number }>;
      // Pick the amount bucket matching the session's presentment currency, then fall back to first available
      const priceEntry = (sessionCurrency ? currencyOpts[sessionCurrency] : undefined)
        ?? currencyOpts[Object.keys(currencyOpts)[0]];
      const amount = priceEntry?.amount ?? 0;
      return {
        id: li.id as string,
        variantId: li.variantId as string,
        priceId: li.priceId as string,
        productName: (product.name ?? '') as string,
        variantName: (variant.name ?? '') as string,
        quantity: (li.quantity ?? 1) as number,
        unitAmount: amount,
        totalAmount: amount * ((li.quantity ?? 1) as number),
        currency: (sessionCurrency ?? 'USD') as string,
        imageUrl: (variant.imageUrl ?? product.imageUrl ?? '') as string,
      };
    });

    const summaryItems = (summary.items ?? []) as Array<Record<string, unknown>>;

    return {
      id: cs.id as string,
      storeId: cs.storeId as string,
      checkoutToken: cs.checkoutToken as string,
      currency: (sessionCurrency ?? 'USD') as string,
      items,
      totals: {
        subtotal: (summary.subtotalAmount ?? 0) as number,
        discount: (summary.totalPromotionAmount ?? 0) as number,
        shipping: (summary.shippingCost ?? 0) as number,
        tax: (summary.totalTaxAmount ?? 0) as number,
        // `totalAdjustedAmount` is what gets charged (subtotal − discounts + shipping + tax).
        // The raw adjuster summary also carries `totalAmount` = subtotal + shipping only —
        // reading that first showed a pre-discount, pre-tax total (#TAG-2246). The legacy
        // `orderSummaries[0]` shape only has `totalAmount`, already aliased to the adjusted total.
        total: (summary.totalAdjustedAmount ??
          summary.totalAmount ??
          summaryItems.reduce((s: number, i: Record<string, unknown>) => s + ((i.amount ?? 0) as number), 0)) as number,
        currency: (sessionCurrency ?? 'USD') as string,
      },
      customer: customer
        ? {
            id: customer.id as string,
            email: customer.email as string | undefined,
            firstName: customer.firstName as string | undefined,
            lastName: customer.lastName as string | undefined,
            phone: customer.phone as string | undefined,
          }
        : undefined,
      shippingAddress: this.normalizeAddress(cs.shippingAddress),
      billingAddress: this.normalizeAddress(cs.billingAddress),
      selectedShippingRateId: (cs.shippingRateId ?? null) as string | null,
      metadata: cs.metadata as Record<string, unknown>,
    };
  }

  /**
   * Map the raw API address shape (`address1`, `address2`, `postal`) onto
   * the SDK's `Address` shape (`line1`, `line2`, `postalCode`). Without
   * this, `session.shippingAddress.line1` is silently undefined after a
   * reload — the type was lying about the actual key names — which is
   * what makes the demo lose form state on refresh.
   */
  private normalizeAddress(raw: unknown): Address | undefined {
    if (!raw || typeof raw !== 'object') return undefined;
    const a = raw as Record<string, unknown>;
    const out: Address = {
      firstName: a.firstName as string | undefined,
      lastName: a.lastName as string | undefined,
      line1: (a.line1 ?? a.address1) as string | undefined,
      line2: (a.line2 ?? a.address2) as string | undefined,
      city: a.city as string | undefined,
      state: a.state as string | undefined,
      postalCode: (a.postalCode ?? a.postal) as string | undefined,
      country: a.country as string | undefined,
      phone: a.phone as string | undefined,
    };
    // If every field is undefined the API returned an empty address slot.
    // Hand back undefined so consumers can `if (session.shippingAddress)`.
    return Object.values(out).some((v) => v !== undefined && v !== '') ? out : undefined;
  }
}

/**
 * Read `checkoutToken` and session token from a URL — standalone alias of
 * {@link CheckoutModule.parseTokensFromUrl}, importable without the class:
 *
 * ```ts
 * import { parseTokensFromUrl } from '@tagadapay/headless-sdk';
 * const tokens = parseTokensFromUrl(); // null if no checkoutToken in the URL
 * ```
 */
export const parseTokensFromUrl = CheckoutModule.parseTokensFromUrl;

/**
 * Remove keys with empty-string / null / undefined values from an object.
 * Prevents sending `""` to APIs that reject empty values (e.g. Stripe `country`).
 */
function stripEmpty<T extends Record<string, unknown>>(obj: T): T {
  const result: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v !== undefined && v !== null && v !== '') {
      result[k] = v;
    }
  }
  return (Object.keys(result).length > 0 ? result : undefined) as T;
}
